import React from "react";
import i18n from "i18n";
import { InfoCircle } from "react-bootstrap-icons";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { filterOption } from "models/filterOption";
import { getAdjustedISOString } from "pages/ControlCenter/ContentManagement/contentManagementUtils/contentManagementUtils";
import {
  volunteerData,
  VolunteerReportsDecisionsCount,
  VolunteersListQueryBody,
} from "./volunteerTypes";
import DisableCameraCheckBox from "./DisableCameraCheckbox";
import DisableUserAppCheckbox from "./DisableUserAppCheckbox";
import UnlinkCameraButton from "./UnlinkCameraButton";

const getFormattedDate = (dateString: string) => {
  if (!dateString) {
    return "";
  }
  const date = new Date(dateString);
  return Intl.DateTimeFormat("he-IS", {
    dateStyle: "short",
    timeZone: "Asia/Jerusalem",
  }).format(date);
};

const getTooltip = (VolunteerReportsData: VolunteerReportsDecisionsCount) => {
  return (
    <Tooltip id="tooltip" style={{ fontSize: "16px" }}>
      <div className="content">
        <div>{`${i18n.t("infoLettersCount")}: ${
          VolunteerReportsData.notificationLetterSent
        }`}</div>
        <div>{`${i18n.t("rejectionsCount")}: ${
          VolunteerReportsData.reportsRejected
        }`}</div>
        <div>{`${i18n.t("sentToPoliceCount")}: ${
          VolunteerReportsData.reportsSentToPolice
        }`}</div>
        <div>{`${i18n.t("seriousLettersCount")}: ${
          VolunteerReportsData.seriousNotificationLetterSent
        }`}</div>
      </div>
    </Tooltip>
  );
};

export const volunteerColumns = [
  {
    title: i18n.t("volunteerId"),
    dataIndex: 1,
    flex: 1,
    renderCell: (volunteer: volunteerData) => (
      <span>{volunteer?.userApp?.id}</span>
    ),
    maxWidth: 80,
  },
  {
    title: i18n.t("volunteerName"),
    dataIndex: 2,
    flex: 1,
    renderCell: (volunteer: volunteerData) => (
      <span>{volunteer?.userApp?.fullName}</span>
    ),
  },
  {
    title: i18n.t("phone"),
    dataIndex: 3,
    flex: 1,
    renderCell: (volunteer: volunteerData) => (
      <span className="phoneTableContainer">
        {volunteer?.userApp?.phoneNumber}
      </span>
    ),
  },
  {
    title: i18n.t("email"),
    dataIndex: 4,
    flex: 1,
    renderCell: (volunteer: volunteerData) => (
      <span>{volunteer?.userApp?.email}</span>
    ),
  },
  {
    title: i18n.t("tz"),
    dataIndex: 5,
    flex: 1,
    renderCell: (volunteer: volunteerData) => (
      <span>{volunteer?.userApp?.tz}</span>
    ),
  },
  {
    title: i18n.t("startDate"),
    dataIndex: 6,
    flex: 1,
    renderCell: (volunteer: volunteerData) => {
      const formatted = getFormattedDate(volunteer?.userApp?.downloadTime);
      return <span>{formatted}</span>;
    },
  },
  {
    title: i18n.t("status"),
    dataIndex: 7,
    flex: 1,
    renderCell: (volunteer: volunteerData) => (
      <span>
        {volunteer?.userApp?.isVerifyByMimshalZamin
          ? i18n.t("verified")
          : i18n.t("notVerified")}
      </span>
    ),
  },
  {
    title: i18n.t("operationSystem"),
    dataIndex: 8,
    flex: 1,
    renderCell: (volunteer: volunteerData) => (
      <span>
        {[...new Set(volunteer?.userApp?.operationSystem)].join(", ")}
      </span>
    ),
  },
  {
    title: i18n.t("currentVersion"),
    dataIndex: 9,
    flex: 1,
    renderCell: (volunteer: volunteerData) => (
      <span>{volunteer?.userApp?.currentVersion.join(", ")}</span>
    ),
  },

  {
    title: i18n.t("lastUpdate"),
    dataIndex: 10,
    flex: 1,
    renderCell: (volunteer: volunteerData) => {
      const formatted = getFormattedDate(volunteer?.userApp?.lastReportUpdate);
      return <span>{formatted}</span>;
    },
  },
  {
    title: i18n.t("reportCount"),
    dataIndex: 11,
    flex: 1,
    renderCell: (volunteer: volunteerData) => {
      return (
        <OverlayTrigger
          overlay={getTooltip(volunteer?.volunteerReportsDecisionsCount)}
          placement="bottom"
        >
          <InfoCircle />
        </OverlayTrigger>
      );
    },
    maxWidth: 80,
  },
  {
    title: i18n.t("isEntitledCamera"),
    dataIndex: 12,
    flex: 1,
    renderCell: (volunteer: volunteerData) => {
      return <DisableCameraCheckBox volunteer={volunteer} />;
    },
  },
  {
    title: i18n.t("cameraUuid"),
    dataIndex: 13,
    flex: 1,
    renderCell: (volunteer: volunteerData) => (
      <span>{volunteer?.userApp?.cameraUuid}</span>
    ),
  },
  {
    title: i18n.t("cameraUuidStartDate"),
    dataIndex: 14,
    flex: 1,
    renderCell: (volunteer: volunteerData) => {
      const dateToFormat = volunteer?.userApp?.cameraUuidStartDate;
      return <span>{dateToFormat ? getFormattedDate(dateToFormat) : ""}</span>;
    },
  },
  {
    title: i18n.t("usersCharacteristic"),
    dataIndex: 15,
    flex: 1,
    renderCell: (volunteer: volunteerData) => {
      return <span>{volunteer?.userApp?.usersCharacteristic}</span>;
    },
  },
  {
    title: i18n.t("חסום?"),
    dataIndex: 16,
    flex: 1,
    renderCell: (volunteer: volunteerData) => {
      return <DisableUserAppCheckbox volunteer={volunteer} />;
    },
    maxWidth: 80,
  },
  {
    title: i18n.t("unlinkCamera"),
    dataIndex: 18,
    flex: 1,
    renderCell: (volunteer: volunteerData) => {
      return <UnlinkCameraButton volunteer={volunteer} />;
    },
    maxWidth: 100,
  },
];

export const initialVolunteerBody: VolunteersListQueryBody = {};
export const formatObjetToNeededType = (filtersOptions: filterOption) => {
  const formated: VolunteersListQueryBody = filtersOptions.options?.reduce(
    (result, object) => {
      const formatedObject = result;

      if (object.isSelected) {
        if (
          object?.field === "registrationDate" ||
          object?.field === "lastReportUpdate"
        ) {
          formatedObject[object.field] = getAdjustedISOString(
            object.value as Date
          );
        } else {
          formatedObject[object.field] = object.value;
        }
      }

      return formatedObject;
    },
    {}
  );

  return formated;
};
